import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardDialogFramePart, DialogAction, DialogContext, DialogService } from '@eforall/dashboard';
import { DialogMode, StaffRoleId } from '@interfaces';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { EngagementEditorDialog, EngagementEditorDialogData } from '../../engagement-editor';
import { EngagementViewerDialog, EngagementViewerDialogData } from '../../engagement-viewer';
import { FollowUpDialogService } from '../follow-up-dialog.service';
import { FollowUpEditorDialog, FollowUpEditorDialogData } from '../follow-up-editor';
import { FollowUpDetailsPart } from './follow-up-details/follow-up-details.part';

export interface FollowUpViewerDialogData {
	followUpId: number;
	staffRoleId?: StaffRoleId;
}

@Component({
	selector: 'follow-up-viewer-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		TabStripModule,
		FollowUpDetailsPart
	],
	templateUrl: './follow-up-viewer.dialog.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './follow-up-viewer.dialog.scss'
})
export class FollowUpViewerDialog implements OnInit {

	public dialogRef = inject(MatDialogRef<FollowUpViewerDialog>);
	public dialogContext: DialogContext<FollowUpViewerDialogData> = inject(MAT_DIALOG_DATA);

	private readonly followUpService = inject(FollowUpDialogService);
	private readonly dialogService = inject(DialogService);

	public readonly headerName = computed(() => 'Follow Up: ' + (this.followUpService.followUpDetails()?.personName || ''));

	async ngOnInit() {
		await this.loadFollowUpDetails();
	}

	private async loadFollowUpDetails() {
		if (this.dialogContext.data?.followUpId) {
			await this.followUpService.getFollowUpById(this.dialogContext.data.followUpId);
		}
	}

	public actions = computed<DialogAction[]>(() => {
		const data = this.dialogContext.data;
		if (!data) return [];

		const actions: DialogAction[] = [
			{
				id: 'close',
				enabled: signal(true),
				visible: signal(true),
				cssClass: signal('btn-standard'),
				text: signal('Cancel'),
				linkType: 'no-action',
				willCloseDialog: true,
			}
		];

		const details = this.followUpService.followUpDetails();
		if (details) {
			if (details.closedUTC && details.followedupEngagementId) {
				actions.unshift({
					id: 'viewEngagement',
					enabled: signal(true),
					visible: signal(true),
					cssClass: signal('btn-standard'),
					text: signal('View Engagement'),
					linkType: 'callback',
					willCloseDialog: false,
					callback: async () => {
						const dialogData: EngagementViewerDialogData = {
							engagementId: details.followedupEngagementId!,
							staffRoleId: data.staffRoleId
						};
						await this.dialogService.showCustom(EngagementViewerDialog, { data: dialogData }, 700, 700);
					}
				});
			} else if (!details.closedUTC) {
				actions.unshift({
					id: 'closeWithEngagement',
					enabled: signal(true),
					visible: signal(true),
					cssClass: signal('btn-standard'),
					text: signal('Close with Engagement'),
					linkType: 'callback',
					willCloseDialog: false,
					callback: async () => {
						const dialogData: EngagementEditorDialogData = {
							mode: DialogMode.ADD,
							staffRoleId: data.staffRoleId,
							followUpId: data.followUpId
						};
						const dialogAction = await this.dialogService.showCustom(EngagementEditorDialog, { data: dialogData }, 650, 800);

						// If save succeeds, callbackResult contains the created engagementId; otherwise, it is undefined.
						if (dialogAction?.callbackResult) {
							await this.loadFollowUpDetails();
						}
					}
				});
				actions.unshift({
					id: 'editFollowUp',
					enabled: signal(true),
					visible: signal(true),
					cssClass: signal('btn-standard'),
					text: signal('Edit'),
					linkType: 'callback',
					willCloseDialog: false,
					callback: async () => {
						const dialogData: FollowUpEditorDialogData = {
							mode: DialogMode.EDIT,
							followUpId: data.followUpId,
						};
						await this.dialogService.showCustom(FollowUpEditorDialog, { data: dialogData }, 650, 750);
						await this.loadFollowUpDetails();
					}
				});

			}
		}

		return actions;
	});
}